import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, FileText, Shield, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface LinkData {
  id: string;
  file_id: string;
  email: string;
  expires_at: string;
  nda_signed: boolean;
  revoked: boolean;
  data_room_files: {
    file_name: string;
    company_id: string;
  };
  companies?: {
    name: string;
  };
}

export default function DataRoomAccess() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState<LinkData | null>(null);
  const [error, setError] = useState('');
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (token) {
      loadLink();
    } else {
      setError('Invalid access link');
      setLoading(false);
    }
  }, [token]);

  async function loadLink() {
    try {
      const { data, error } = await supabase
        .from('data_room_links')
        .select('*, data_room_files!inner(file_name, company_id, companies!inner(name))')
        .eq('token', token)
        .single();

      if (error) throw error;

      if (!data) {
        setError('Link not found');
      } else if (data.revoked) {
        setError('This link has been revoked');
      } else if (new Date(data.expires_at) < new Date()) {
        setError('This link has expired');
      } else {
        // Transform the data to match our interface
        const transformedData = {
          ...data,
          data_room_files: {
            file_name: data.data_room_files.file_name,
            company_id: data.data_room_files.company_id,
          },
          companies: data.data_room_files.companies,
        };
        setLink(transformedData as any);
      }
    } catch (err: any) {
      console.error('Error loading link:', err);
      setError('Failed to load access link');
    } finally {
      setLoading(false);
    }
  }

  async function acceptNDA() {
    if (!link || !token) return;

    setAccepting(true);
    try {
      const { error } = await supabase.functions.invoke('accept-dataroom-nda', {
        body: { token },
      });

      if (error) throw error;

      toast.success('NDA accepted. You can now access the document.');
      setLink({ ...link, nda_signed: true });
    } catch (err: any) {
      console.error('Error accepting NDA:', err);
      toast.error(err.message || 'Failed to accept NDA');
    } finally {
      setAccepting(false);
    }
  }

  function openDocument() {
    if (!link) return;
    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    window.open(`${baseUrl}/functions/v1/dr-open?lid=${link.id}`, '_blank');
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading secure document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
              <h2 className="text-xl font-semibold">Access Denied</h2>
              <p className="text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!link) return null;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-3xl mx-auto py-12 space-y-6">
        <div className="text-center space-y-2">
          <Shield className="h-12 w-12 text-primary mx-auto" />
          <h1 className="text-3xl font-bold">Secure Document Access</h1>
          <p className="text-muted-foreground">
            You've been granted access to a confidential document
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Company</span>
                <span className="font-medium">{link.companies?.name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Document</span>
                <span className="font-medium">{link.data_room_files.file_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Recipient</span>
                <span className="font-medium">{link.email}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-muted-foreground">Link Expires</span>
                <span className="font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {new Date(link.expires_at).toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {!link.nda_signed ? (
          <Card className="border-warning">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Non-Disclosure Agreement Required
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You must accept the NDA before accessing this confidential document.
                </AlertDescription>
              </Alert>

              <div className="bg-muted p-6 rounded-lg space-y-4 max-h-[400px] overflow-y-auto">
                <h3 className="font-semibold text-lg">Non-Disclosure Agreement</h3>
                <div className="space-y-3 text-sm">
                  <p>
                    This Non-Disclosure Agreement (the "Agreement") is entered into as of{' '}
                    {new Date().toLocaleDateString()} by and between the disclosing party and the
                    receiving party ({link.email}).
                  </p>
                  <p className="font-semibold">1. Confidential Information</p>
                  <p>
                    The receiving party acknowledges that all documents, data, and information
                    shared through this secure link are confidential and proprietary to the
                    disclosing party.
                  </p>
                  <p className="font-semibold">2. Obligations</p>
                  <p>
                    The receiving party agrees to: (a) maintain the confidentiality of all
                    information; (b) not disclose information to any third party; (c) use
                    information solely for the intended business purpose; (d) protect the
                    information with the same degree of care used for their own confidential
                    information.
                  </p>
                  <p className="font-semibold">3. Term</p>
                  <p>
                    This Agreement shall remain in effect for a period of five (5) years from the
                    date of acceptance or until the information becomes publicly available through
                    no fault of the receiving party.
                  </p>
                  <p className="font-semibold">4. Access Tracking</p>
                  <p>
                    Your access to this document is monitored and logged, including IP address,
                    location, access time, and duration for security and compliance purposes.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="nda-accept"
                    checked={ndaAccepted}
                    onCheckedChange={(checked) => setNdaAccepted(checked === true)}
                  />
                  <label
                    htmlFor="nda-accept"
                    className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I have read and agree to the terms of this Non-Disclosure Agreement
                  </label>
                </div>

                <Button
                  onClick={acceptNDA}
                  disabled={!ndaAccepted || accepting}
                  className="w-full"
                  size="lg"
                >
                  {accepting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Shield className="h-4 w-4 mr-2" />
                  )}
                  Accept NDA and Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-green-600">
            <CardContent className="pt-6">
              <div className="text-center space-y-6">
                <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">NDA Accepted</h3>
                  <p className="text-muted-foreground">
                    You can now access the document. Your access is being monitored for security.
                  </p>
                </div>
                <Button onClick={openDocument} size="lg" className="w-full sm:w-auto">
                  <FileText className="h-4 w-4 mr-2" />
                  View Document
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3 text-sm">
              <Shield className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium">Security & Privacy</p>
                <p className="text-muted-foreground">
                  This document is protected by enterprise-grade security. Access is logged and
                  monitored. Do not share this link or document with unauthorized parties.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
